/*
 * FORGOT PASSWORD (decision 607): ask for a reset link.
 *
 * THE ANSWER IS THE SAME SENTENCE EVERY TIME. The server answers 202 whether or
 * not an account uses the address, so the page cannot know either -- and must
 * not pretend to. "If an account uses that address, a link is on its way" is
 * the honest sentence for every case, including the one where nothing was sent.
 *
 * A failure to REACH the server is the one thing worth saying differently,
 * because then the request genuinely did not happen and trying again helps.
 */

import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Brand } from "../components/Brand";
import { ApiError, api } from "../lib/api";
import styles from "./SignIn.module.css";

export function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.requestPasswordReset(email.trim());
      setSentTo(email.trim());
    } catch (err) {
      setError(
        err instanceof ApiError && err.failure.kind === "offline"
          ? t("auth.errorOffline")
          : t("auth.errorGeneric"),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <Brand variant="lockup" height={34} />
        </div>
        <h1 className={styles.heading}>{t("auth.forgot.heading")}</h1>

        {sentTo ? (
          <p className={styles.done} role="status">
            {t("auth.forgot.sent", { email: sentTo })}
          </p>
        ) : (
          <>
            <p className={styles.intro}>{t("auth.forgot.intro")}</p>
            <form className={styles.form} onSubmit={(e) => void submit(e)}>
              <label className={styles.field}>
                <span className={styles.label}>{t("auth.email")}</span>
                <input
                  className={styles.input}
                  type="email"
                  name="email"
                  // `username`: the token a password manager keys the account
                  // identifier on, so it fills the address it has saved.
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              {error ? (
                <p className={styles.error} role="alert">
                  {error}
                </p>
              ) : null}
              <button className={styles.primary} type="submit" disabled={busy}>
                {t("auth.forgot.action")}
              </button>
            </form>
          </>
        )}

        <p className={styles.alt}>
          <Link to="/">{t("auth.backToSignIn")}</Link>
        </p>
      </div>
    </div>
  );
}
